import React from 'react';
import { Heart, Briefcase, Users, Home, Scale, Brain, AlertTriangle, Activity, Shield, UserCheck } from 'lucide-react';

const PROFILE_SECTIONS = [
  { id: 'crisis', label: 'Crisis & Why Now', icon: AlertTriangle, color: 'red' },
  { id: 'substance_use', label: 'Substance Use', icon: Activity, color: 'amber' },
  { id: 'health_diet', label: 'Health, Diet & Fitness', icon: Heart, color: 'rose' },
  { id: 'mental_health', label: 'Mental Health', icon: Brain, color: 'violet' },
  { id: 'home', label: 'Living Situation', icon: Home, color: 'blue' },
  { id: 'job_finances', label: 'Employment & Income', icon: Briefcase, color: 'amber' },
  { id: 'treatment', label: 'Treatment History', icon: Shield, color: 'emerald' },
  { id: 'friends', label: 'Friends & Social', icon: Users, color: 'teal' },
  { id: 'family', label: 'Family & Relationships', icon: UserCheck, color: 'indigo' },
  { id: 'legal', label: 'Legal Matters', icon: Scale, color: 'zinc' },
];

function StickmanVisualization({ data = {}, userName = '', size = 'normal' }) {
  const isSmall = size === 'small';
  const isFemale = data.gender === 'female';

  const generateSummary = (sectionId) => {
    const d = data;
    switch (sectionId) {
      case 'crisis': {
        const items = [];
        if (d.crisis_why_now) items.push(d.crisis_why_now.substring(0, 120) + (d.crisis_why_now.length > 120 ? '...' : ''));
        if (d.crisis_last_drink_date) items.push(`Last drink/drug: ${d.crisis_last_drink_date}`);
        if (d.crisis_legal_issue === 'yes') items.push('Current legal issue');
        if (d.crisis_using_for_court === 'yes') items.push('Court sentencing support');
        return items;
      }
      case 'substance_use': {
        const items = [];
        if (d.primary_drug) items.push(`Primary: ${d.primary_drug}`);
        if (d.secondary_drugs?.length) items.push(`Secondary: ${d.secondary_drugs.join(', ')}`);
        if (d.method_of_use?.length) items.push(`Method: ${d.method_of_use.join(', ')}`);
        if (d.ever_injected === 'yes') items.push('History of injection');
        return items;
      }
      case 'health_diet': {
        const items = [];
        if (d.has_major_health_issues === 'yes') items.push(`Health: ${d.health_issue_details || 'Issues reported'}`);
        if (d.medication_allergies === 'yes') items.push(`Med allergies: ${d.medication_allergy_details || 'Yes'}`);
        if (d.diet_vegetarian === 'yes') items.push('Vegetarian/Vegan');
        if (d.diet_food_allergies === 'yes') items.push(`Food allergies: ${d.diet_allergy_details || 'Yes'}`);
        if (d.diet_diabetic === 'yes') items.push(`Diabetic: ${d.diet_diabetic_type || ''} (${d.diet_diabetic_management || ''})`);
        if (d.diet_regular_meals === 'no') items.push('Irregular meals');
        if (d.chronic_conditions === 'yes') items.push(`Chronic: ${d.chronic_condition_details || 'Yes'}`);
        return items;
      }
      case 'mental_health': {
        const items = [];
        if (d.has_mental_health_diagnosis === 'yes') items.push('Diagnosed');
        if (d.mh_admissions?.length) items.push(`${d.mh_admissions.length} treatment(s)`);
        if (d.currently_receiving_mh_treatment === 'yes') items.push('Currently in treatment');
        if (d.medication_details) items.push(`Meds: ${d.medication_details.substring(0, 60)}`);
        return items;
      }
      case 'home': {
        const items = [];
        if (d.living_arrangement) items.push(d.living_arrangement);
        if (d.usual_accommodation) items.push(d.usual_accommodation);
        if (d.living_how_long) items.push(`Duration: ${d.living_how_long}`);
        return items;
      }
      case 'job_finances': {
        const items = [];
        if (d.is_employed === 'yes') {
          items.push(`Employed: ${d.employer || ''} (${d.profession || ''})`);
          if (d.years_in_industry) items.push(`${d.years_in_industry} in industry`);
        } else if (d.is_employed === 'no') items.push('Currently unemployed');
        if (d.has_own_business === 'yes') items.push('Business owner');
        if (d.lost_business_due_to_addiction === 'yes') items.push('Lost business to addiction');
        if (d.work_environment_dangerous === 'yes') items.push('Dangerous work environment');
        return items;
      }
      case 'treatment': {
        const items = [];
        if (d.has_previous_treatment === 'yes') {
          const count = d.treatment_entries?.length || 0;
          items.push(`${count || 1} previous treatment(s)`);
          if (d.treatment_entries?.[0]) {
            const t = d.treatment_entries[0];
            items.push(`Most recent: ${t.type || ''} at ${t.location || 'N/A'}`);
          }
        } else if (d.has_previous_treatment === 'no') items.push('No previous treatment');
        return items;
      }
      case 'friends': {
        const items = [];
        if (d.has_good_friends === 'yes') items.push('Has friends');
        if (d.has_good_friends === 'no') items.push('No close friends');
        if (d.friends_use_drugs === 'yes') items.push(`Friends use: ${d.friends_use_amount || 'Yes'}`);
        if (d.family_drug_use === 'yes') items.push('Family drug/alcohol use');
        return items;
      }
      case 'family': {
        const items = [];
        if (d.indigenous_status === 'yes') items.push(`Indigenous: ${d.indigenous_details || 'Yes'}`);
        if (d.gender) items.push(`Gender: ${d.gender}`);
        if (d.age_group) items.push(`Age: ${d.age_group}`);
        if (d.country_of_birth) items.push(`Born: ${d.country_of_birth}`);
        return items;
      }
      case 'legal': {
        const items = [];
        if (d.has_legal_matters === 'yes') {
          items.push('Current legal matters');
          if (d.charge_types?.length) items.push(`Charges: ${d.charge_types.join(', ')}`);
          if (d.court_date_pending === 'yes') items.push('Court date pending');
        }
        if (d.previous_legal_history === 'yes') {
          items.push(`${d.previous_charges?.length || 0} previous charge(s)`);
        }
        return items;
      }
      default: return [];
    }
  };

  const colorMap = {
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', heading: 'text-red-600' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', heading: 'text-amber-600' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', heading: 'text-rose-600' },
    violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', heading: 'text-violet-600' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', heading: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', heading: 'text-emerald-600' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', heading: 'text-teal-600' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', heading: 'text-indigo-600' },
    zinc: { bg: 'bg-zinc-50', border: 'border-zinc-200', text: 'text-zinc-700', heading: 'text-zinc-600' },
  };

  return (
    <div className={`${isSmall ? 'max-w-xs' : 'max-w-2xl'} mx-auto`} data-testid="stickman-profile">
      {/* Header */}
      <div className="bg-blue-600 text-white rounded-xl p-4 mb-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70 mb-1">Personal Profile</p>
        <h3 className={`${isSmall ? 'text-lg' : 'text-xl'} font-bold`}>
          {userName || 'User'}
        </h3>
        {data.gender && <p className="text-xs opacity-70 mt-0.5 capitalize">{data.gender} &middot; {data.age_group || ''}</p>}
      </div>

      {/* Section Cards */}
      <div className={`grid ${isSmall ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
        {PROFILE_SECTIONS.map((section) => {
          const items = generateSummary(section.id);
          const c = colorMap[section.color] || colorMap.zinc;
          const Icon = section.icon;
          const hasData = items.length > 0;

          return (
            <div key={section.id} className={`${c.bg} border ${c.border} rounded-xl ${isSmall ? 'p-2' : 'p-3'}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon className={`${isSmall ? 'w-3 h-3' : 'w-3.5 h-3.5'} ${c.heading}`} />
                <span className={`${isSmall ? 'text-[10px]' : 'text-xs'} font-bold uppercase tracking-wider ${c.heading}`}>{section.label}</span>
              </div>
              {hasData ? (
                <ul className={`space-y-0.5 ${isSmall ? 'text-[9px]' : 'text-[11px]'} ${c.text}`}>
                  {items.map((item, i) => (
                    <li key={i} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              ) : (
                <p className={`${isSmall ? 'text-[9px]' : 'text-[11px]'} text-gray-400 italic`}>Not recorded</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default StickmanVisualization;
